import { useTranslation } from 'react-i18next';
import { Customer } from './types';

interface CustomersListProps {
    customers: Customer[];
    onEdit: (customer: Customer) => void;
    onDelete: (id: number) => void;
}

export const CustomersList = ({ customers, onEdit, onDelete }: CustomersListProps) => {
    const { t } = useTranslation() as { t: (key: string) => string };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800">{t('common.label.lista-customers')}</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {customers.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <svg
                            className="mx-auto h-12 w-12 text-gray-400 mb-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                        </svg>
                        <p className="text-sm">{t('common.label.no-customers')}</p>
                    </div>
                ) : (
                    customers.map((customer) => (
                        <div
                            key={customer.id}
                            className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all bg-white"
                            style={{ 
                                borderLeftWidth: '4px', 
                                borderLeftColor: customer.is_acquired ? '#10b981' : '#f59e0b' 
                            }}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 flex-1 break-words">{customer.name}</h4>
                                <div
                                    className={`w-3 h-3 rounded-full flex-shrink-0 ml-2 ${
                                        customer.is_acquired ? 'bg-green-500' : 'bg-yellow-500'
                                    }`}
                                />
                            </div>

                            {customer.address && (
                                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{customer.address}</p>
                            )}

                            {customer.lat && customer.lon && (
                                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                    <span className="flex items-center gap-1">
                                        <svg
                                            className="w-3 h-3"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                        </svg>
                                        {customer.lat.toFixed(4)}, {customer.lon.toFixed(4)}
                                    </span>
                                </div>
                            )}

                            <div className="flex items-center justify-between">
                                <span
                                    className={`text-xs font-semibold px-2 py-1 rounded ${
                                        customer.is_acquired 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}
                                >
                                    {customer.is_acquired 
                                        ? `✓ ${t('common.form-label.es-cliente')}`
                                        : `○ ${t('common.label.no-adquirido')}`
                                    }
                                </span>

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => onEdit(customer)}
                                        className="p-1.5 text-[#006bc6] hover:bg-blue-50 rounded transition-all"
                                        title={t('common.label.edit')}
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                            />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => customer.id && onDelete(customer.id)}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-all"
                                        title={t('common.label.delete')}
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
